import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Autocomplete,
  Tooltip,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Popover,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { TABLE_CONTAINER_STYLE, NUMERICAL_COLUMN_WIDTH, AMOUNT_COLUMN_WIDTH } from '../../constants/invoiceConstants';
import { useTheme } from '@mui/material/styles';
import { formatNumber, debounce } from '../../utils/invoiceCalculations';
import { useTranslation } from '../../hooks/useTranslation';

const ITEM_PREFIX_REGEX = /^Item[\s\-_:]+/i;

const cleanItemNameStr = (str) => {
  if (!str || typeof str !== 'string') return '';
  const trimmed = str.trim();
  if (ITEM_PREFIX_REGEX.test(trimmed)) {
    return trimmed.replace(ITEM_PREFIX_REGEX, '').trim();
  }
  return trimmed;
};

const getItemOptionLabel = (option) => {
  if (!option) return '';
  const rawName = typeof option === 'string' ? option : (option.name || option.productName || option.itemName || option.description || option.itemCode || '');
  return cleanItemNameStr(rawName);
};

const checkOptionEqualToValue = (option, value) => {
  if (!option || !value) return false;
  const cleanOpt = typeof option === 'string' ? cleanItemNameStr(option) : cleanItemNameStr(option.name || option.productName || option.itemName || option.description || '');
  const cleanVal = typeof value === 'string' ? cleanItemNameStr(value) : cleanItemNameStr(value.name || value.productName || value.itemName || value.description || '');

  if (typeof value === 'string') return cleanOpt === cleanVal || option.id === value || option._id === value;
  if (typeof option === 'string') return cleanOpt === cleanVal || option === value.id || option === value._id;
  return (option.id || option._id) === (value.id || value._id) || (cleanOpt !== '' && cleanOpt === cleanVal);
};


const PurchaseReturnItemsTable = ({
  items,
  onItemChange,
  onRemoveItem,
  visibleColumns,
  products,
  onSettingsClick,
  readOnly = false
}) => {
   const { t } = useTranslation();
  // State for settings popover
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const settingsOpen = Boolean(settingsAnchorEl);

  // State for error message
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);


  // Calculate totals for an item
  const calculateItemTotals = (item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const netValue = qty * price;
    const discountAmount = (netValue * (Number(item.discount) || 0)) / 100;
    const netValueAfterDiscount = netValue - discountAmount;
    const vatRate = Number(item.vatRate) || 0;
    const vatAmount = (netValueAfterDiscount * vatRate) / 100;
    const total = netValueAfterDiscount + vatAmount;

    return {
      netValue,
      discountAmount,
      netValueAfterDiscount,
      vatAmount,
      total
    };
  };

  // Calculate totals for the summary row
  const calculateTotals = () => {
    return items.reduce((totals, item) => {
      const itemTotals = calculateItemTotals(item);
      const qty = Number(item.quantity) || 0;
      totals.quantity += qty;
      totals.lineTotal += qty * (Number(item.price) || 0);
      totals.discountAmount += itemTotals.discountAmount || 0;
      totals.netValue += itemTotals.netValueAfterDiscount || 0;
      totals.vatAmount += itemTotals.vatAmount || 0;
      totals.grandTotal += itemTotals.total || 0;
      return totals;
    }, {
      quantity: 0,
      lineTotal: 0,
      discountAmount: 0,
      netValue: 0,
      vatAmount: 0,
      grandTotal: 0
    });
  };

  const totals = calculateTotals();

  // Handle settings icon click
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
    if (onSettingsClick) {
      onSettingsClick(event);
    }
  };

  // Handle settings popover close
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  // Handle focus on input fields
  const handleFocus = (event) => {
    event.target.select();
  };

  // Handle column toggle
  const handleColumnToggle = (column) => {
    if (onSettingsClick) {
      // Call parent's onSettingsClick with column information
      const event = { target: { name: column, value: !visibleColumns[column] } };
      onSettingsClick(event);
    }
  };

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          ...TABLE_CONTAINER_STYLE,
          overflow: 'auto',
          maxHeight: '60vh',
          marginBottom: '24px',
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
            padding: '4px 8px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.primary',
            height: '36px',
          },
          '& .MuiTableCell-head': {
            backgroundColor: (theme) => theme.palette.background.default,
            fontWeight: 600,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        }}

      >
        <Table
          size="small"
          sx={{
            width: '100%',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                width="100%"
                align="left"
                sx={{
                  fontWeight: 600,
                  padding: '4px 8px',
                }}
              >
                {t('Common.Item')}
              </TableCell>
              <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>{t('Common.Qty')}</TableCell>
              <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('Common.Unit_Price')}</TableCell>
              {visibleColumns.priceWithVat && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('Common.Unit_Price')}</TableCell>}
              <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}> {t('Common.Total_Amt')}</TableCell>
              {visibleColumns.discountPerc && <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>{t('Common.Discount_Percent')}%</TableCell>}
              {visibleColumns.discountAmt && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}> {t('Common.Discount_Amt')}</TableCell>}
              {visibleColumns.netValue && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('Common.Net_Value')}</TableCell>}
              <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>{t('Common.VAT_Percent')}</TableCell>
              {visibleColumns.vatAmount && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}> {t('Common.VAT_Amt')}</TableCell>}
              {visibleColumns.grandTotal && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>{t('Common.Grand_Total')}</TableCell>}
              {visibleColumns.description && <TableCell align="center">{t('Common.Description')}</TableCell>}
              <TableCell align="center" width="50px">
                <Tooltip title={t('Common.Column_Settings')}>
                  <IconButton
                    size="small"
                    onClick={handleSettingsClick}
                    sx={{ p: 0.5 }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const totals = calculateItemTotals(item);
              const hasLimit = item.originalQuantity !== undefined && item.originalQuantity !== null && !isNaN(Number(item.originalQuantity));
              const maxQty = hasLimit ? Number(item.originalQuantity) : null;
              const isNegative = item.quantity !== '' && item.quantity !== null && item.quantity !== undefined && Number(item.quantity) < 0;
              const isExceeding = hasLimit && item.quantity !== '' && item.quantity !== null && item.quantity !== undefined && Number(item.quantity) > maxQty;
              const isInvalid = isNegative || isExceeding;
              const itemName = item.product?.name || item.description || cleanItemNameStr(item.itemName) || t('Common.Item');

              return (
                <TableRow key={item.id}>
                  <TableCell align="left">
                    <Autocomplete
                      options={products}
                      getOptionLabel={getItemOptionLabel}
                      isOptionEqualToValue={checkOptionEqualToValue}

                      value={item.product || null}
                      onChange={(event, newValue) => onItemChange(item.id, 'product', newValue)}
                      disabled={readOnly}
                      size="small"
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          borderRadius: 0,
                          height: '32px',
                          width: '100%',
                          '& fieldset': {
                            border: 'none'
                          }
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          variant="standard"
                          InputProps={{
                            ...params.InputProps,
                            disableUnderline: true
                          }}
                          sx={{
                            width: '100%',
                            '& .MuiInputBase-input': {
                              color: 'text.primary',
                              fontWeight: 500,
                              padding: '4px 8px !important',
                              '&.Mui-disabled': {
                                color: 'text.primary',
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)'
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        isExceeding
                          ? `Exceeds max permitted quantity (${maxQty})`
                          : isNegative
                            ? 'Quantity cannot be negative'
                            : (hasLimit ? `Max allowed: ${maxQty}` : '')
                      }
                      arrow
                    >
                      <TextField
                        value={item.quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newQty = value === '' ? '' : (parseInt(value, 10) || 0);
                          onItemChange(item.id, 'quantity', newQty);

                          if (value !== '') {
                            const numQty = Number(newQty);
                            if (numQty < 0) {
                              setErrorMessage(`Return quantity cannot be negative for "${itemName}". Minimum allowed is 0.`);
                              setShowError(true);
                            } else if (hasLimit && numQty > maxQty) {
                              setErrorMessage(
                                `Quantity ${numQty} exceeds the maximum permitted quantity of ${maxQty} for "${itemName}".`
                              );
                              setShowError(true);
                            } else {
                              setShowError(false);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value === '') return;

                          const currentQty = parseInt(value, 10);
                          if (isNaN(currentQty) || currentQty < 0) {
                            setErrorMessage(`Return quantity cannot be negative for "${itemName}". Minimum allowed is 0.`);
                            setShowError(true);
                          } else if (hasLimit && currentQty > maxQty) {
                            setErrorMessage(
                              `Quantity ${currentQty} exceeds the maximum permitted quantity of ${maxQty} for "${itemName}".`
                            );
                            setShowError(true);
                          }
                        }}
                        type="number"
                        size="small"
                        variant="standard"
                        error={isInvalid}
                        InputProps={{
                          disableUnderline: true,
                          inputProps: {
                            min: 0,
                            max: hasLimit ? maxQty : undefined,
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{
                          width: NUMERICAL_COLUMN_WIDTH,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                          },
                          '& input': {
                            MozAppearance: 'textfield',
                            color: isInvalid ? 'error.main' : 'text.primary',
                            fontWeight: 500
                          },
                          '& .MuiInputBase-root': {
                            border: isInvalid ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '4px',
                            paddingX: '4px',
                            backgroundColor: isInvalid ? 'rgba(211, 47, 47, 0.04)' : 'transparent',
                            transition: 'border-color 0.2s ease, background-color 0.2s ease',
                          },
                          '& .MuiInputBase-root:hover': {
                            borderColor: isInvalid ? '#d32f2f' : '#1976d2',
                          },
                          '& .MuiInputBase-root.Mui-focused': {
                            borderColor: isInvalid ? '#d32f2f' : '#1976d2',
                            backgroundColor: isInvalid ? 'rgba(211, 47, 47, 0.08)' : '#e3f2fd',
                          },
                        }}
                        onFocus={(e) => e.target.select()}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(item.price)}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue === '' || !isNaN(parseFloat(newValue))) {
                          onItemChange(item.id, 'price', parseFloat(newValue) || 0);
                        }
                      }}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        inputProps: {
                          min: 0,
                          step: 0.01,
                          style: {
                            textAlign: 'center',
                            color: 'rgba(0, 0, 0, 0.87)',
                            opacity: 1,
                            '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.87)'
                          }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                          color: 'text.primary',
                          fontWeight: 500
                        },
                        '& .MuiInputBase-root': {
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                      disabled={readOnly}
                      onFocus={handleFocus}
                    />
                  </TableCell>
                  {visibleColumns.priceWithVat && (
                    <TableCell align="center">
                      <TextField
                        value={formatNumber(item.price * (1 + (Number(item.vatRate) || 0) / 100))}
                        type="text"
                        size="small"
                        variant="standard"
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                          inputProps: {
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{
                          width: AMOUNT_COLUMN_WIDTH,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                          },
                          '& input': {
                            MozAppearance: 'textfield',
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.netValue)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                  {visibleColumns.discountPerc && (
                    <TableCell align="center">
                      <TextField
                        value={formatNumber(item.discount)}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          if (newValue === '' || !isNaN(parseFloat(newValue))) {
                            onItemChange(item.id, 'discount', parseFloat(newValue) || 0);
                          }
                        }}
                        type="text"
                        size="small"
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          inputProps: {
                            min: 0,
                            max: 100,
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{
                          width: NUMERICAL_COLUMN_WIDTH,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                          },
                          '& input': {
                            MozAppearance: 'textfield',
                          },
                          '& .MuiInputBase-root': {
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '4px',
                            paddingX: '4px',
                            transition: 'border-color 0.2s ease',
                          },
                          '& .MuiInputBase-root:hover': {
                            borderColor: '#1976d2',
                          },
                          '& .MuiInputBase-root.Mui-focused': {
                            borderColor: '#1976d2',
                            backgroundColor: '#e3f2fd',
                          },
                        }}
                        disabled={readOnly}
                        onFocus={handleFocus}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.discountAmt && (
                    <TableCell align="center">
                      <TextField
                        value={formatNumber(totals.discountAmount)}
                        type="text"
                        size="small"
                        variant="standard"
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                          inputProps: {
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{
                          width: AMOUNT_COLUMN_WIDTH,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                          },
                          '& input': {
                            MozAppearance: 'textfield',
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.netValue && (
                    <TableCell align="center">
                      <TextField
                        value={formatNumber(totals.netValueAfterDiscount)}
                        type="text"
                        size="small"
                        variant="standard"
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                          inputProps: {
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{
                          width: AMOUNT_COLUMN_WIDTH,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                          },
                          '& input': {
                            MozAppearance: 'textfield',
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(item.vatRate)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: NUMERICAL_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                  {visibleColumns.vatAmount && (
                    <TableCell align="center">
                      <TextField
                        value={formatNumber(totals.vatAmount)}
                        type="text"
                        size="small"
                        variant="standard"
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                          inputProps: {
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{
                          width: AMOUNT_COLUMN_WIDTH,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                          },
                          '& input': {
                            MozAppearance: 'textfield',
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.grandTotal && (
                    <TableCell align="center">
                      <TextField
                        value={formatNumber(totals.total)}
                        type="text"
                        size="small"
                        variant="standard"
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                          inputProps: {
                            style: { textAlign: 'center' }
                          }
                        }}
                        sx={{
                          width: AMOUNT_COLUMN_WIDTH,
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                          },
                          '& input': {
                            MozAppearance: 'textfield',
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.description && (
                    <TableCell align="center">
                      <TextField
                        value={item.description}
                        onChange={(e) => onItemChange(item.id, 'description', e.target.value)}
                        size="small"
                        variant="standard"
                        fullWidth
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          '& .MuiInputBase-root': {
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: '4px',
                            padding: '0 4px',
                            transition: 'border-color 0.2s ease',
                          },
                          '& .MuiInputBase-root:hover': {
                            borderColor: '#1976d2',
                          },
                          '& .MuiInputBase-root.Mui-focused': {
                            borderColor: '#1976d2',
                            backgroundColor: '#e3f2fd',
                          },
                        }}
                        disabled={readOnly}
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onRemoveItem(item.id)}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Totals Row */}
            <TableRow sx={{
              position: 'sticky',
              bottom: 0,
              bgcolor: '#f1f4fd',
              borderTop: '2px solid rgba(0, 0, 0, 0.12)',
              '& td': {
                fontWeight: 'bold',
                fontSize: '0.875rem',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              }
            }}>
              <TableCell align="left"> {t('Common.Line_Totals')}</TableCell>
              <TableCell align="center">
                {formatNumber(totals.quantity)}
              </TableCell>
              <TableCell align="center">
                {items.length > 0 ? formatNumber(items.reduce((sum, item) => sum + (Number(item.price) || 0), 0) / items.length) : '0.00'}
              </TableCell>
              {visibleColumns.priceWithVat && (
                <TableCell align="center">
                  {items.length > 0 ? formatNumber(items.reduce((sum, item) => sum + ((Number(item.price) || 0) * (1 + (Number(item.vatRate) || 0) / 100)), 0) / items.length) : '0.00'}
                </TableCell>
              )}
              <TableCell align="center">
                {formatNumber(totals.lineTotal)}
              </TableCell>
              {visibleColumns.discountPerc && (
                <TableCell align="center">
                  {formatNumber((items.reduce((sum, item) => sum + (Number(item.discount) || 0), 0) /
                    Math.max(items.length, 1)))}
                </TableCell>
              )}
              {visibleColumns.discountAmt && (
                <TableCell align="center">
                  {formatNumber(totals.discountAmount)}
                </TableCell>
              )}
              {visibleColumns.netValue && (
                <TableCell align="center">
                  {formatNumber(totals.netValue)}
                </TableCell>
              )}
              <TableCell align="center">
                {formatNumber((items.reduce((sum, item) => sum + (Number(item.vatRate) || 0), 0) /
                  Math.max(items.length, 1)))}
              </TableCell>
              {visibleColumns.vatAmount && (
                <TableCell align="center">
                  {formatNumber(totals.vatAmount)}
                </TableCell>
              )}
              {visibleColumns.grandTotal && (
                <TableCell align="center">
                  {formatNumber(totals.grandTotal)}
                </TableCell>
              )}
              {visibleColumns.description && <TableCell />}
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Column Settings Popover */}
      <Popover
        open={settingsOpen}
        anchorEl={settingsAnchorEl}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List>
          {Object.entries(visibleColumns).map(([column, visible]) => (
            <ListItem key={column} button onClick={() => handleColumnToggle(column)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={visible}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1')} />
            </ListItem>
          ))}
        </List>
      </Popover>

      {/* Error Message Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PurchaseReturnItemsTable;
